import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import useApiHelper from "../hooks/useApiHelper";
import { DefaultResponseWithData } from "../models/system";
import {
  EbayPolicyKind,
  IOfferUpdateBatchStatus,
  IPolicyInUse,
  IPolicyReplacementPreview,
  IPolicyReplacementResult,
} from "../models/OfferUpdate";

interface IReplacePolicyBody {
  kind: EbayPolicyKind;
  fromPolicyId: string;
  toPolicyId: string;
  // A trial: only this many live listings, nothing else touched.
  limit?: number;
}

// Admin > Replace eBay Policy. Every endpoint behind it is AdminOnly on the API.
export default function usePolicyReplacement(
  kind: EbayPolicyKind,
  fromPolicyId?: string,
  toPolicyId?: string,
  batchId?: string | null
) {
  const { get, post } = useApiHelper();
  const queryClient = useQueryClient();

  const inUse = useQuery({
    queryKey: ["policy-replacement-in-use", kind],
    queryFn: () =>
      get<DefaultResponseWithData<IPolicyInUse[]>>(`/offerupdate/replace-policy/in-use?kind=${kind}`),
  });

  const preview = useQuery({
    queryKey: ["policy-replacement-preview", kind, fromPolicyId, toPolicyId],
    queryFn: () =>
      get<DefaultResponseWithData<IPolicyReplacementPreview>>(
        `/offerupdate/replace-policy/preview?kind=${kind}&fromPolicyId=${encodeURIComponent(fromPolicyId!)}` +
          (toPolicyId ? `&toPolicyId=${encodeURIComponent(toPolicyId)}` : "")
      ),
    enabled: Boolean(fromPolicyId),
  });

  // Polls the live-listing batch until the queue receiver has finished every item.
  const batch = useQuery({
    queryKey: ["offer-update-batch", batchId],
    queryFn: () => get<DefaultResponseWithData<IOfferUpdateBatchStatus>>(`/offerupdate/batch/${batchId}`),
    enabled: Boolean(batchId),
    refetchInterval: (data) => (data?.data?.isComplete ? false : 5000),
    // The live listings only leave the old policy as the receiver revises them, so recount once it's done.
    onSuccess: (data) => {
      if (data?.data?.isComplete) {
        queryClient.invalidateQueries(["policy-replacement-in-use"]);
        queryClient.invalidateQueries(["policy-replacement-preview"]);
      }
    },
  });

  const replace = useMutation(
    (body: IReplacePolicyBody) =>
      post<IReplacePolicyBody, DefaultResponseWithData<IPolicyReplacementResult>>("/offerupdate/replace-policy", body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["policy-replacement-in-use"]);
        queryClient.invalidateQueries(["policy-replacement-preview"]);
      },
    }
  );

  return {
    inUse,
    preview,
    batch,
    replace,
  };
}
